import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';

import i18n from 'i18n-js';
import moment from 'moment';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import Layout from 'components/Layout';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Switch, FormControlLabel, Card } from '@mui/material';
import PropTypes from 'prop-types';
import { forceTimezone, toQueryString } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentsActions } from 'state/modules/documents';
import { DocumentTypesSelect } from 'components/AsyncFields';
import VersionStatus from 'components/Documents/VersionStatus';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { debounce } from 'lodash';
import { styled } from '@mui/material/styles';
import { Table, DateRangePicker } from 'components/UI';

const SwitchWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  paddingLeft: theme.spacing(2),
}));

const WidgetHeader = ({
  endDate,
  startDate,
  onlyExpired,
  handleParams,
  documentTypeId,
  handleCreationDateHandler,
}) => {
  const [isExpired, setExpired] = useState(onlyExpired && onlyExpired !== 'false');
  const handleToggle = () => {
    setExpired(!isExpired);
    handleParams('is_expired', !isExpired);
  };
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
        }}
      >
        <Box
          sx={{
            flexBasis: { xs: '100%', md: '16.6667%' },
            minWidth: 0,
          }}
        >
          <DocumentTypesSelect
            form={{ setFieldValue: handleParams }}
            margin="none"
            field={{ name: 'document_type_id', value: documentTypeId || '' }}
            firstLoad={!!documentTypeId}
            label={i18n.t('documents.document_type')}
          />
        </Box>
        <Box
          sx={{
            width: 'auto',
            minWidth: 0,
          }}
        >
          <DateRangePicker
            showClearDates
            onDatesChange={handleCreationDateHandler}
            label={i18n.t('documents.creation_date')}
            endDate={endDate ? moment(endDate) : undefined}
            startDate={startDate ? moment(startDate) : undefined}
          />
        </Box>
        <Box
          sx={{
            flexBasis: { xs: '40%', md: '25%', lg: '33.3333%' },
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <SwitchWrapper>
            <FormControlLabel
              value="start"
              control={
                <Switch
                  color="primary"
                  checked={isExpired}
                  onChange={handleToggle}
                  slotProps={{ input: { 'aria-label': i18n.t('documents.show_only_expired') } }}
                />
              }
              label={
                <span style={{ fontSize: '13px' }}>{i18n.t('documents.show_only_expired')}</span>
              }
              labelPlacement="start"
            />
          </SwitchWrapper>
        </Box>
      </Box>
    </Box>
  );
};

WidgetHeader.propTypes = {
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onlyExpired: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  handleParams: PropTypes.func.isRequired,
  documentTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleCreationDateHandler: PropTypes.func.isRequired,
};

function DocumentsPage() {
  const urlParams = useQuery();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });
  const history = useHistory();
  const dispatch = useDispatch();
  const documents = useSelector((state) =>
    state.documents.all.map((id) => state.documents.byId[id])
  );
  const pagination = useSelector((state) => state.documents.pagination);
  const isFetching = useSelector((state) => state.documents.loading);
  const { localeFormat } = useSelector((state) => state.organizations);
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const columns = useMemo(
    () => [
      {
        name: i18n.t('tableColumns.id'),
        selector: (row) => row.id,
        sortable: true,
        sortField: 'id',
        grow: 0,
      },
      {
        name: i18n.t('tableColumns.document_type'),
        selector: (row) => row.document_type.name,
        grow: 1,
      },
      {
        name: i18n.t('tableColumns.related_to'),
        selector: (row) => row.source_id,
        cell: (row) => `${row.source_type} #${row.source_id}`,
        grow: 1,
      },
      {
        name: i18n.t('tableColumns.expiration_date'),
        selector: (row) => row.default.expiration_time,
        cell: (row) => {
          if (!row?.default?.expiration_time) {
            return '--';
          }
          const expiration = moment(row?.default?.expiration_time);
          return (
            <Box display="inline-flex" flexDirection="row" alignItems="center">
              {expiration.format(localeFormat)}
              {Boolean(expiration.isSameOrBefore()) && (
                <Box display="inline-flex" flexDirection="row" alignItems="center" ml={2}>
                  <NotificationsIcon color="error" fontSize="small" />
                  <Typography color="error" component="span" variant="caption">
                    {i18n.t('documents.expired')}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        },
        grow: 1,
      },
      {
        name: i18n.t('tableColumns.creation_date'),
        selector: (row) => row.created_time,
        cell: (row) => (row?.created_time ? moment(row?.created_time).format(localeFormat) : '--'),
        sortable: true,
        sortField: 'created_time',
        grow: 1,
      },
      {
        name: i18n.t('common.status'),
        selector: (row) => row.status,
        cell: (row) =>
          row?.default?.status ? <VersionStatus status={row?.default?.status} /> : '--',
        grow: 1,
      },
    ],
    [] // eslint-disable-line
  );

  const handleRowClicked = (row) => {
    history.push(buildUrl(`/documents/${row?.id}`));
  };

  const fetchDocuments = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        DocumentsActions.fetchDocuments(params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchDocumentsDebounced = useCallback(
    debounce((params) => fetchDocuments(params), 1000),
    [fetchDocuments]
  );

  useEffect(() => {
    if (isMounted.current) {
      fetchDocumentsDebounced(params);
    } else {
      isMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchDocuments(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleParams = (params) => {
    const data = { ...params };
    setParams({ ...data });
    const _params = toQueryString(data);
    history.replace(buildUrl(`/documents${_params}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.sortField,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const handleCreationDateHandler = (dates) => {
    const { startDate, endDate } = dates;
    let _params = { ...params, page: 1 };
    if (startDate) {
      _params.start_date = forceTimezone(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:00Z');
    } else {
      delete _params.start_date;
    }
    if (!endDate && startDate) {
      _params.end_date = forceTimezone(startDate).endOf('day').format('YYYY-MM-DDTHH:mm:00Z');
    } else if (endDate && startDate) {
      _params.end_date = forceTimezone(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:00Z');
    } else {
      delete _params.end_date;
    }

    handleParams(_params);
  };

  // search by status, keyword and organization
  const onSearch = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    handleParams({ ...filters });
  };

  return (
    <Layout title={i18n.t('sidebar.documents')}>
      <Grid container spacing={2} style={{ paddingBottom: '25px' }}>
        <Grid size={{ xs: 12 }}>
          <Grid
            container
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid size={{ sm: 8 }}>
              <Title dataCy="documents-title" title={i18n.t('sidebar.documents')} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <WidgetHeader
          handleParams={onSearch}
          keyword={params.keyword}
          endDate={params.end_date}
          startDate={params.start_date}
          onlyExpired={params.is_expired}
          documentTypeId={params.document_type_id}
          handleCreationDateHandler={handleCreationDateHandler}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Card sx={{ mt: '32px' }}>
          <Paper>
            <Table
              data={documents}
              columns={columns}
              page={Number(params.page)}
              onSort={handleSort}
              limit={Number(params.limit)}
              handlePageChange={handlePageChange}
              handleRowClicked={handleRowClicked}
              defaultSortAsc={!params?.sort_desc}
              defaultSortField={params?.sort_field}
              totalRows={pagination?.totalRows || 0}
              handlePerRowsChange={handlePerRowsChange}
              loading={isFetching}
            />
          </Paper>
        </Card>
      </Grid>
    </Layout>
  );
}

export default DocumentsPage;
